import { useEffect, useMemo, useRef } from 'react';
import { Group, Layer, Line, Rect, Stage, Text } from 'react-konva';
import type Konva from 'konva';
import { CANVAS, FONT_FAMILY } from '../../constants';
import { EdgeComponent } from '../edges/EdgeComponent';
import { computeProxySockets, GroupFrameComponent, ProxySocket } from '../nodes/GroupFrameComponent';
import { NodeComponent } from '../nodes/NodeComponent';
import { byContainment, byNestingDepth } from '../../utils/nesting';
import { FragmentComponent } from '../sequence/FragmentComponent';
import { MessageComponent } from '../sequence/MessageComponent';
import { ParticipantComponent } from '../sequence/ParticipantComponent';
import { computeForceAdjustedControlPoints, AdjustedControlPoints } from '../../utils/forceEdgeLayout';
import { computeOrthoMidShifts, computeOrthoPaths } from '../../utils/edgeMath';
import { buildEdgeTints } from '../../utils/edgeTint';
import { computeLifelineBottom } from '../../utils/contentBounds';
import { assignSequenceMessageNumbers, resolveSequenceMessageLabelWidth } from '../../utils/sequenceMessageLayout';
import { resolveMessageColor, resolveParticipantColor, resolveSequenceAppearanceMode, resolveSequencePalette } from '../../utils/sequenceAppearance';
import type { JumperPoint } from '../../utils/edgeMath';
import type {
  EdgeRoutingMode,
  IconStyle,
  Node as DiagramNode,
  SequenceParticipant,
  SocketLabelVisibility,
  ThemePalette,
} from '../../types';
import type { ExportDiagramSnapshot } from '../../utils/exportBounds';

interface DiagramExportStageProps {
  snapshot: ExportDiagramSnapshot;
  stageWidth: number;
  stageHeight: number;
  viewport: {
    x: number;
    y: number;
    scale: number;
  };
  palette: ThemePalette;
  iconStyle: IconStyle;
  socketLabelVisibility: SocketLabelVisibility;
  defaultRoutingMode: EdgeRoutingMode;
  /** The page's label multiplier, so an export matches what the canvas shows. */
  textScale?: number;
  forceLayoutEdges: boolean;
  /** Colour wires by the frame they land in, as the canvas does. */
  tintEdgesByDestination?: boolean;
  sequenceMessageNumbering?: boolean;
  monochrome?: boolean;
  onStageReady?: (stage: Konva.Stage) => void;
}

const NOOP = () => {};

export function DiagramExportStage({
  snapshot,
  stageWidth,
  stageHeight,
  viewport,
  palette,
  iconStyle,
  socketLabelVisibility,
  defaultRoutingMode,
  textScale = 1,
  forceLayoutEdges,
  tintEdgesByDestination = true,
  sequenceMessageNumbering = true,
  monochrome = false,
  onStageReady,
}: DiagramExportStageProps) {
  const stageRef = useRef<Konva.Stage>(null);

  useEffect(() => {
    if (stageRef.current) {
      onStageReady?.(stageRef.current);
    }
  }, [onStageReady]);

  const flowNodes = snapshot.nodes;
  const flowEdges = snapshot.edges;
  const groups = snapshot.groups;
  const layers = snapshot.layers;
  const participants = snapshot.participants;
  const messages = snapshot.messages;
  const fragments = snapshot.fragments;
  const appearanceMode = resolveSequenceAppearanceMode({ exportMonochrome: monochrome });
  const sequencePalette = resolveSequencePalette(appearanceMode, palette);
  const exportPalette = snapshot.diagramType === 'sequence' ? sequencePalette : palette;
  const messageNumbers = useMemo(
    () => (sequenceMessageNumbering ? assignSequenceMessageNumbers(messages) : null),
    [messages, sequenceMessageNumbering],
  );
  const participantGroups = snapshot.participantGroups;
  const lifelineBottom = useMemo(
    () => computeLifelineBottom(messages, fragments, snapshot.seqLifelineLength),
    [messages, fragments, snapshot.seqLifelineLength],
  );

  const gridLines = useMemo(() => {
    const { x: vx, y: vy, scale: vs } = viewport;
    const startX = Math.floor(-vx / vs / CANVAS.GRID_SIZE) * CANVAS.GRID_SIZE;
    const endX = Math.ceil((stageWidth - vx) / vs / CANVAS.GRID_SIZE) * CANVAS.GRID_SIZE;
    const startY = Math.floor(-vy / vs / CANVAS.GRID_SIZE) * CANVAS.GRID_SIZE;
    const endY = Math.ceil((stageHeight - vy) / vs / CANVAS.GRID_SIZE) * CANVAS.GRID_SIZE;
    const lines: JSX.Element[] = [];
    for (let x = startX; x <= endX; x += CANVAS.GRID_SIZE) {
      const sx = x * vs + vx;
      lines.push(
        <Line
          key={`grid-v-${x}`}
          points={[sx, 0, sx, stageHeight]}
          stroke={exportPalette.gridLine}
          strokeWidth={1}
          listening={false}
        />,
      );
    }
    for (let y = startY; y <= endY; y += CANVAS.GRID_SIZE) {
      const sy = y * vs + vy;
      lines.push(
        <Line
          key={`grid-h-${y}`}
          points={[0, sy, stageWidth, sy]}
          stroke={exportPalette.gridLine}
          strokeWidth={1}
          listening={false}
        />,
      );
    }
    return lines;
  }, [exportPalette.gridLine, stageHeight, stageWidth, viewport]);

  const connectedSockets = useMemo(() => {
    const connected = new Set<string>();
    flowEdges.forEach((edge) => {
      connected.add(`${edge.sourceNodeId}-${edge.sourceSocketId}`);
      connected.add(`${edge.targetNodeId}-${edge.targetSocketId}`);
    });
    return connected;
  }, [flowEdges]);

  const getNodeLayerOpacity = (node: { layerId?: string }) => {
    const layer = layers.find((entry) => entry.id === (node.layerId ?? 'default'))
      ?? layers.find((entry) => entry.id === 'default');
    if (!layer) return 1;
    if (!layer.visible) return 0;
    return layer.dimmed ? 0.2 : 1;
  };

  const isEdgeVisible = (edge: { sourceNodeId: string; targetNodeId: string }) => {
    const source = flowNodes.find((node) => node.id === edge.sourceNodeId);
    const target = flowNodes.find((node) => node.id === edge.targetNodeId);
    const sourceLayer = layers.find((layer) => layer.id === (source?.layerId ?? 'default'));
    const targetLayer = layers.find((layer) => layer.id === (target?.layerId ?? 'default'));
    return (sourceLayer?.visible ?? true) && (targetLayer?.visible ?? true);
  };

  const proxyData = useMemo(() => {
    const proxySocketMap = new Map<string, ProxySocket[]>();
    const proxySocketLookup = new Map<string, { groupId: string; proxy: ProxySocket }>();
    const collapsedChildIds = new Set<string>();

    groups.forEach((group) => {
      const treatAsCollapsed = group.isFrame ? group.collapsed : true;
      if (!treatAsCollapsed) return;
      group.childNodeIds.forEach((id) => collapsedChildIds.add(id));

      if (!group.isFrame && (group.groupInputNodeId || group.groupOutputNodeId)) {
        const proxies: ProxySocket[] = [];
        const inputNode = group.groupInputNodeId
          ? flowNodes.find((node) => node.id === group.groupInputNodeId)
          : undefined;
        const outputNode = group.groupOutputNodeId
          ? flowNodes.find((node) => node.id === group.groupOutputNodeId)
          : undefined;

        let inputIndex = 0;
        let outputIndex = 0;

        inputNode?.sockets.filter((socket) => socket.type === 'input').forEach((socket) => {
          const proxy: ProxySocket = {
            id: `proxy-in-${group.id}-${socket.id}`,
            type: 'input',
            label: socket.label ?? `In ${inputIndex + 1}`,
            position: { x: 0, y: 40 + inputIndex * 22 + 11 },
            originalNodeId: inputNode.id,
            originalSocketId: socket.id,
          };
          proxies.push(proxy);
          proxySocketLookup.set(`${inputNode.id}:${socket.id}`, { groupId: group.id, proxy });
          inputIndex += 1;
        });

        outputNode?.sockets.filter((socket) => socket.type === 'output').forEach((socket) => {
          const proxy: ProxySocket = {
            id: `proxy-out-${group.id}-${socket.id}`,
            type: 'output',
            label: socket.label ?? `Out ${outputIndex + 1}`,
            position: { x: 220, y: 40 + outputIndex * 22 + 11 },
            originalNodeId: outputNode.id,
            originalSocketId: socket.id,
          };
          proxies.push(proxy);
          proxySocketLookup.set(`${outputNode.id}:${socket.id}`, { groupId: group.id, proxy });
          outputIndex += 1;
        });

        proxySocketMap.set(group.id, proxies);
      } else {
        const proxies = computeProxySockets(group, flowNodes, flowEdges);
        proxySocketMap.set(group.id, proxies);
        proxies.forEach((proxy) => {
          proxySocketLookup.set(`${proxy.originalNodeId}:${proxy.originalSocketId}`, {
            groupId: group.id,
            proxy,
          });
        });
      }
    });

    return {
      proxySocketMap,
      proxySocketLookup,
      collapsedChildIds,
    };
  }, [flowEdges, flowNodes, groups]);

  const orthoMidShifts = useMemo(
    () => computeOrthoMidShifts(flowEdges, flowNodes, defaultRoutingMode),
    [defaultRoutingMode, flowEdges, flowNodes],
  );

  const jumperPoints = useMemo(() => [] as JumperPoint[], []);

  const forceAdjustedCPs = useMemo<AdjustedControlPoints>(() => {
    if (!forceLayoutEdges) return {};
    const getSocket = (nodeId: string, socketId: string) => {
      const node = flowNodes.find((entry) => entry.id === nodeId);
      return node?.sockets.find((socket) => socket.id === socketId);
    };
    return computeForceAdjustedControlPoints(flowEdges, flowNodes, getSocket, 4, orthoMidShifts);
  }, [flowEdges, flowNodes, forceLayoutEdges, orthoMidShifts]);

  // Same set-wide routing the canvas uses, so an export matches what was on screen.
  const orthoPaths = useMemo(
    () => computeOrthoPaths(flowEdges, flowNodes, defaultRoutingMode, orthoMidShifts),
    [flowEdges, flowNodes, defaultRoutingMode, orthoMidShifts],
  );

  const edgeTints = useMemo(
    () => (tintEdgesByDestination ? buildEdgeTints(flowEdges, flowNodes, groups) : {}),
    [tintEdgesByDestination, flowEdges, flowNodes, groups],
  );

  const visibleTopLevelGroups = useMemo(() => ([
    ...groups.filter((group) => group.isFrame),
    ...groups.filter((group) => {
      if (group.isFrame) return false;
      if (!group.parentGroupId) return true;
      const parent = groups.find((entry) => entry.id === group.parentGroupId);
      return parent?.isFrame === true;
    }),
  ]), [groups]);

  const sequenceParticipants = useMemo(() => {
    const isParticipantVisible = (participant: { layerId?: string }) => {
      const layer = layers.find((entry) => entry.id === (participant.layerId ?? 'default'));
      return layer?.visible ?? true;
    };

    const collapsedGroupIds = new Set(participantGroups.filter((group) => group.collapsed).map((group) => group.id));
    const collapsedParticipantIds = new Set(
      participantGroups.filter((group) => group.collapsed).flatMap((group) => group.childParticipantIds),
    );
    const visibleParticipants = participants.filter((participant) =>
      isParticipantVisible(participant) && !collapsedParticipantIds.has(participant.id),
    );

    const proxyParticipants = participantGroups
      .filter((group) => group.collapsed)
      .map((group) => {
        const children = participants.filter((participant) => group.childParticipantIds.includes(participant.id));
        if (children.length === 0) return null;
        const avgX = children.reduce((sum, participant) => sum + participant.x, 0) / children.length;
        return {
          group,
          proxy: {
            id: group.id,
            type: children[0].type,
            label: group.label,
            color: group.color,
            order: Math.min(...children.map((participant) => participant.order)),
            x: avgX,
          } as SequenceParticipant,
        };
      })
      .filter((entry): entry is { group: typeof participantGroups[number]; proxy: SequenceParticipant } => entry !== null);

    const allVisibleParticipants = [...visibleParticipants, ...proxyParticipants.map((entry) => entry.proxy)];

    const resolveParticipant = (participantId: string) => {
      if (collapsedParticipantIds.has(participantId)) {
        const group = participantGroups.find((entry) =>
          entry.collapsed && entry.childParticipantIds.includes(participantId),
        );
        if (group) {
          return allVisibleParticipants.find((participant) => participant.id === group.id) ?? null;
        }
      }
      return allVisibleParticipants.find((participant) => participant.id === participantId) ?? null;
    };

    const selfMessageLabelWidthByParticipantId = new Map<string, number>(
      allVisibleParticipants.map((participant) => ([
        participant.id,
        resolveSequenceMessageLabelWidth(
          allVisibleParticipants,
          participant.id,
          participant.id,
          96,
        ),
      ])),
    );

    return {
      collapsedGroupIds,
      resolveParticipant,
      visibleParticipants,
      proxyParticipants,
      selfMessageLabelWidthByParticipantId,
      isParticipantVisible,
    };
  }, [layers, participantGroups, participants]);

  return (
    <Stage ref={stageRef} width={stageWidth} height={stageHeight}>
      <Layer>
        <Rect x={0} y={0} width={stageWidth} height={stageHeight} fill={exportPalette.canvasBg} listening={false} />
        {gridLines}

        <Group x={viewport.x} y={viewport.y} scaleX={viewport.scale} scaleY={viewport.scale}>
          {snapshot.diagramType === 'flow' && (
            <>
              {/* Same paint order as the canvas: frames, the nodes they hold,
                  then the edges over both. An export that stacks them any other
                  way is not a picture of what the author was looking at. */}
              {visibleTopLevelGroups
                .slice()
                .sort(byNestingDepth(groups, (group) => group.id))
                .map((group) => (
                <GroupFrameComponent
                  textScale={textScale}
                  key={group.id}
                  group={group.isFrame ? group : { ...group, collapsed: true }}
                  isSelected={false}
                  proxySockets={proxyData.proxySocketMap.get(group.id) ?? []}
                  onGroupClick={NOOP}
                  onGroupDoubleClick={NOOP}
                  onGroupContextMenu={NOOP}
                  onDragMove={NOOP}
                  palette={palette}
                  scale={viewport.scale}
                  isFrame={group.isFrame}
                  isReadOnly
                />
              ))}

              {flowNodes
                .filter((node) => {
                  const layer = layers.find((entry) => entry.id === (node.layerId ?? 'default'));
                  if (!(layer?.visible ?? true)) return false;
                  if (node.parentGroupId) {
                    const group = groups.find((entry) => entry.id === node.parentGroupId);
                    if (group && !group.isFrame) return false;
                    if (group?.isFrame && group.collapsed) return false;
                  }
                  return true;
                })
                .sort(byContainment(flowNodes, groups))
                .map((node) => (
                  <NodeComponent
                    textScale={textScale}
                    key={node.id}
                    node={node}
                    onUpdate={NOOP}
                    connectedSockets={connectedSockets}
                    isConnecting={false}
                    isSelected={false}
                    onSocketMouseDown={NOOP}
                    onSocketMouseUp={NOOP}
                    onNodeClick={NOOP}
                    layerOpacity={getNodeLayerOpacity(node)}
                    iconStyle={iconStyle}
                    palette={palette}
                    socketLabelVisibility={socketLabelVisibility}
                    isReadOnly
                    canDrag={false}
                  />
                ))}
              {flowEdges
                .filter(isEdgeVisible)
                .filter((edge) => {
                  const sourceInCollapsed = proxyData.collapsedChildIds.has(edge.sourceNodeId);
                  const targetInCollapsed = proxyData.collapsedChildIds.has(edge.targetNodeId);
                  if (sourceInCollapsed && targetInCollapsed) {
                    const sourceGroup = groups.find((group) => !group.isFrame && group.childNodeIds.includes(edge.sourceNodeId));
                    const targetGroup = groups.find((group) => !group.isFrame && group.childNodeIds.includes(edge.targetNodeId));
                    if (sourceGroup && targetGroup && sourceGroup.id === targetGroup.id) {
                      return false;
                    }
                  }
                  return true;
                })
                .map((edge) => {
                  const sourceProxy = proxyData.proxySocketLookup.get(`${edge.sourceNodeId}:${edge.sourceSocketId}`);
                  const targetProxy = proxyData.proxySocketLookup.get(`${edge.targetNodeId}:${edge.targetSocketId}`);

                  let effectiveSource: DiagramNode | null = null;
                  let effectiveTarget: DiagramNode | null = null;
                  let effectiveEdge = edge;

                  if (sourceProxy) {
                    const group = groups.find((entry) => entry.id === sourceProxy.groupId);
                    if (!group) return null;
                    effectiveSource = {
                      id: group.id,
                      type: 'group',
                      position: group.position,
                      size: { width: 220, height: 100 },
                      label: group.label,
                      sockets: [{
                        id: sourceProxy.proxy.id,
                        type: 'output',
                        dataType: 'data',
                        position: sourceProxy.proxy.position,
                      }],
                    };
                    effectiveEdge = {
                      ...effectiveEdge,
                      sourceNodeId: group.id,
                      sourceSocketId: sourceProxy.proxy.id,
                    };
                  } else {
                    effectiveSource = flowNodes.find((node) => node.id === edge.sourceNodeId) ?? null;
                  }

                  if (targetProxy) {
                    const group = groups.find((entry) => entry.id === targetProxy.groupId);
                    if (!group) return null;
                    effectiveTarget = {
                      id: group.id,
                      type: 'group',
                      position: group.position,
                      size: { width: 220, height: 100 },
                      label: group.label,
                      sockets: [{
                        id: targetProxy.proxy.id,
                        type: 'input',
                        dataType: 'data',
                        position: targetProxy.proxy.position,
                      }],
                    };
                    effectiveEdge = {
                      ...effectiveEdge,
                      targetNodeId: group.id,
                      targetSocketId: targetProxy.proxy.id,
                    };
                  } else {
                    effectiveTarget = flowNodes.find((node) => node.id === edge.targetNodeId) ?? null;
                  }

                  if (!effectiveSource || !effectiveTarget) return null;

                  return (
                    <EdgeComponent
                      key={edge.id}
                      edge={effectiveEdge}
                      sourceNode={effectiveSource}
                      targetNode={effectiveTarget}
                      isSelected={false}
                      onEdgeClick={NOOP}
                      palette={palette}
                      globalRoutingMode={defaultRoutingMode}
                      textScale={textScale}
                      allNodes={flowNodes}
                      jumperPoints={jumperPoints}
                      forceControlPoints={forceAdjustedCPs[edge.id] ?? null}
                      orthoMidShift={orthoMidShifts[edge.id] ?? 0}
                      routedPath={orthoPaths[edge.id]}
                      tint={edgeTints[edge.id]}
                      isReadOnly
                    />
                  );
                })}

            </>
          )}

          {snapshot.diagramType === 'sequence' && (
            <>
              {participantGroups.filter((group) => !group.collapsed).map((group) => {
                const children = participants.filter((participant) =>
                  group.childParticipantIds.includes(participant.id) && sequenceParticipants.isParticipantVisible(participant),
                );
                if (children.length === 0) return null;
                const minX = Math.min(...children.map((participant) => participant.x)) - 20;
                const maxX = Math.max(...children.map((participant) => participant.x + 140)) + 20;
                const groupWidth = maxX - minX;
                return (
                  <Group key={group.id}>
                    <Rect
                      x={minX}
                      y={20}
                      width={groupWidth}
                      height={lifelineBottom + 60}
                      fill={`${group.color}08`}
                      stroke={`${group.color}40`}
                      strokeWidth={1.5}
                      dash={[8, 4]}
                      cornerRadius={8}
                    />
                    <Rect
                      x={minX}
                      y={20}
                      width={Math.min(120, groupWidth)}
                      height={22}
                      fill={group.color}
                      cornerRadius={[8, 0, 8, 0]}
                      listening={false}
                    />
                    <Text
                      text={group.label}
                      x={minX + 8}
                      y={20}
                      height={22}
                      verticalAlign="middle"
                      fontFamily={FONT_FAMILY}
                      fontSize={10}
                      fontStyle="bold"
                      fill="white"
                      listening={false}
                    />
                  </Group>
                );
              })}

              {(() => {
                const sortedFragments: typeof fragments = [];
                const visited = new Set<string>();
                const visitFragment = (fragment: typeof fragments[number]) => {
                  if (visited.has(fragment.id)) return;
                  if (fragment.parentFragmentId) {
                    const parent = fragments.find((entry) => entry.id === fragment.parentFragmentId);
                    if (parent) visitFragment(parent);
                  }
                  visited.add(fragment.id);
                  sortedFragments.push(fragment);
                };
                fragments.forEach(visitFragment);

                const getNestLevel = (fragmentId: string, depth = 0): number => {
                  const fragment = fragments.find((entry) => entry.id === fragmentId);
                  if (!fragment?.parentFragmentId) return depth;
                  return getNestLevel(fragment.parentFragmentId, depth + 1);
                };

                return sortedFragments.map((fragment) => {
                  const fragmentParticipants = participants.filter((participant) =>
                    fragment.participantIds.includes(participant.id),
                  );
                  if (fragmentParticipants.length === 0) return null;
                  const nestLevel = getNestLevel(fragment.id);
                  const inset = nestLevel * 12;
                  const minX = Math.min(...fragmentParticipants.map((participant) => participant.x));
                  const maxX = Math.max(...fragmentParticipants.map((participant) => participant.x + 140));

                  return (
                    <FragmentComponent
                      key={fragment.id}
                      fragment={fragment}
                      x={minX - 20 + inset}
                      width={maxX - minX + 40 - inset * 2}
                      viewport={viewport}
                      isSelected={false}
                      onFragmentClick={NOOP}
                      onFragmentDoubleClick={NOOP}
                      palette={exportPalette}
                      appearanceMode={appearanceMode}
                      nestLevel={nestLevel}
                      altDividerY={fragment.altDividerY}
                      isReadOnly
                    />
                  );
                });
              })()}

              {messages.map((message) => {
                const source = sequenceParticipants.resolveParticipant(message.sourceId);
                const target = sequenceParticipants.resolveParticipant(message.targetId);
                if (!source || !target) return null;
                if (
                  source.id === target.id &&
                  sequenceParticipants.collapsedGroupIds.has(source.id)
                ) {
                  return null;
                }
                if (!sequenceParticipants.isParticipantVisible(participants.find((entry) => entry.id === message.sourceId) ?? { layerId: undefined })) {
                  return null;
                }
                if (!sequenceParticipants.isParticipantVisible(participants.find((entry) => entry.id === message.targetId) ?? { layerId: undefined })) {
                  return null;
                }

                return (
                  <MessageComponent
                    key={message.id}
                    message={{
                      ...message,
                      color: resolveMessageColor(message.color, appearanceMode, exportPalette),
                    }}
                    sourceX={source.x + 70}
                    targetX={target.x + 70}
                    selfMessageLabelMaxWidth={message.sourceId === message.targetId
                      ? (sequenceParticipants.selfMessageLabelWidthByParticipantId.get(source.id) ?? 96)
                      : undefined}
                    isSelected={false}
                    onMessageClick={NOOP}
                    onMessageDoubleClick={NOOP}
                    hideLabel={false}
                    palette={exportPalette}
                    zoomScale={viewport.scale}
                    isReadOnly
                    sequenceNumber={messageNumbers?.get(message.id)}
                  />
                );
              })}

              {sequenceParticipants.proxyParticipants.map(({ proxy }) => (
                <ParticipantComponent
                  key={`proxy-${proxy.id}`}
                  participant={{
                    ...proxy,
                    color: resolveParticipantColor(proxy.color, appearanceMode, exportPalette),
                  }}
                  lifelineBottom={lifelineBottom}
                  isSelected={false}
                  onParticipantClick={NOOP}
                  onParticipantDoubleClick={NOOP}
                  onParticipantDragEnd={NOOP}
                  onLifelineMouseDown={NOOP}
                  onLifelineMouseUp={NOOP}
                  palette={exportPalette}
                  isReadOnly
                  canDrag={false}
                />
              ))}

              {sequenceParticipants.visibleParticipants.map((participant) => {
                const layer = layers.find((entry) => entry.id === (participant.layerId ?? 'default'))
                  ?? layers.find((entry) => entry.id === 'default');
                const layerOpacity = !layer ? 1 : layer.visible ? (layer.dimmed ? 0.2 : 1) : 0;

                return (
                  <ParticipantComponent
                    key={participant.id}
                    participant={{
                      ...participant,
                      color: resolveParticipantColor(participant.color, appearanceMode, exportPalette),
                    }}
                    lifelineBottom={lifelineBottom}
                    isSelected={false}
                    onParticipantClick={NOOP}
                    onParticipantDoubleClick={NOOP}
                    onParticipantDragEnd={NOOP}
                    onLifelineMouseDown={NOOP}
                    onLifelineMouseUp={NOOP}
                    palette={exportPalette}
                    layerOpacity={layerOpacity}
                    isReadOnly
                    canDrag={false}
                  />
                );
              })}
            </>
          )}
        </Group>
      </Layer>
    </Stage>
  );
}
